import { Component, Suspense, type ReactNode } from "react";
import { pick, useT } from "../i18n/index.js";
import { Link } from "../router.js";
import "./async-boundary.css";

interface Props { children: ReactNode; resetKey: string; overlay?: boolean; backToBase?: boolean; onDismiss?: () => void; showFallback?: boolean }

function ExitActions({ backToBase, onDismiss }: Pick<Props, "backToBase" | "onDismiss">) {
  return <>
    {onDismiss ? <button type="button" className="btn" onClick={onDismiss}>{pick({ de: "Schließen", en: "Close" })}</button> : null}
    {backToBase ? <Link className="btn" to="/app">{pick({ de: "Zur Basis", en: "Back to base" })}</Link> : null}
  </>;
}

function Loading({ overlay = false, ...actions }: Pick<Props, "overlay" | "backToBase" | "onDismiss">) {
  const t = useT();
  return <div className={`async-status ${overlay ? "async-overlay" : ""}`} role="status" aria-live="polite" data-testid="route-loading"><span className="spinner" aria-hidden="true" /><p>{t("common.loading")}</p><ExitActions {...actions} /></div>;
}

/** A failed download can recover after a connection loss or a new deployment; navigation remains available. */
class LoadBoundary extends Component<Props, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  override componentDidUpdate(previous: Props) {
    if (previous.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }
  override render() {
    if (!this.state.failed) return this.props.children;
    if (this.props.showFallback === false) return null;
    return <section className={`async-status ${this.props.overlay ? "async-overlay" : ""}`} role="alert" data-testid="route-load-error">
      <strong>{pick({ de: "Dieser Bereich konnte nicht geladen werden.", en: "This section could not be loaded." })}</strong>
      <p>{pick({ de: "Prüfe deine Verbindung und lade die Seite neu.", en: "Check your connection and reload the page." })}</p>
      <button type="button" className="btn primary" onClick={() => location.reload()}>{pick({ de: "Neu laden", en: "Reload" })}</button>
      <ExitActions {...this.props} />
    </section>;
  }
}

export function AsyncBoundary({ children, ...props }: Props) {
  return <LoadBoundary {...props}><Suspense fallback={props.showFallback === false ? null : <Loading {...props} />}>{children}</Suspense></LoadBoundary>;
}
